/**
 * Sheet „Zugriff geben" (`access-add`, Spec 05a, „Sheet Zugriff am Objekt":
 * „öffnet dieselbe Auswahl wie im Einladen-Sheet, aber mit fester `entity_id`
 * und einer Personenauswahl (`access-add-user`) statt E-Mail"). Das Objekt
 * steht fest (aus `AccessSheet`), deshalb bleibt hier nur, was einen Grant
 * ausmacht: Person, Rolle, Ablauf.
 *
 * Rolle und Ablaufdatum tragen eigene Bezeichner (`access-add-role`/
 * `access-add-expires`) — die Spec nennt nur `access-add` und
 * `access-add-user` wörtlich, der Rest folgt der Namensform der übrigen
 * `access-*`-Felder (`access-invite-role`/`access-invite-expires`).
 *
 * `presetPerson` (Spec 05b, „Wenn 409 would_lock_out kommt") belegt die
 * Personenauswahl vor und sperrt sie — dieselbe Form wie `presetEntity` in
 * `AccessInviteSheet`: `object-private-grant-self` öffnet dieses Sheet mit der
 * eigenen Person, damit der Ausweg aus der Aussperr-Bedingung ohne Suche
 * auskommt.
 */

import { useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ApiError } from '@/lib/api'
import { berlinEndOfDayIso } from '@/lib/date'
import { useCreateGrant } from '@/features/access/api'
import { PersonPicker } from '@/features/access/PersonPicker'
import type { PersonSelection } from '@/features/access/PersonPicker'
import { RoleSelectField } from '@/features/access/RoleSelectField'
import { FIELD_CLASS, FormField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'

interface AccessAddGrantSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly entityId: string
  readonly entityLabel: string
  /** Nutzer mit bereits einer direkten Zeile — nicht noch einmal vorgeschlagen. */
  readonly excludeIds: readonly string[]
  /** „Ist die gesuchte Person noch nicht da, führt `access-add-invite` in das
   * Einladen-Sheet, mit dem Objekt bereits gesetzt" — `AccessSheet` besitzt
   * beide Sheets und wechselt zwischen ihnen. */
  readonly onRequestInvite: () => void
  readonly presetPerson?: PersonSelection
}

const DEFAULT_ROLE = 'viewer'

const EMPTY_FORM = {
  person: null as PersonSelection | null,
  role: DEFAULT_ROLE,
  expiresAt: '',
}

type FormValues = typeof EMPTY_FORM

export function AccessAddGrantSheet({
  open,
  onOpenChange,
  entityId,
  entityLabel,
  excludeIds,
  onRequestInvite,
  presetPerson,
}: AccessAddGrantSheetProps) {
  const createMutation = useCreateGrant()

  function initialForm(): FormValues {
    return { ...EMPTY_FORM, person: presetPerson ?? null }
  }

  const [form, setForm] = useState<FormValues>(initialForm)
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)

  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setForm(initialForm())
      setFieldError(undefined)
      setGeneralError(undefined)
    }
  }

  function updateField<K extends keyof FormValues>(name: K) {
    return (value: FormValues[K]) => {
      setForm((current) => ({ ...current, [name]: value }))
    }
  }

  async function submit() {
    if (form.person === null) return
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      await createMutation.mutateAsync({
        entity_id: entityId,
        user_id: form.person.id,
        role: form.role,
        ...(form.expiresAt === '' ? {} : { expires_at: berlinEndOfDayIso(form.expiresAt) }),
      })
      onOpenChange(false)
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    await submit()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[420px] md:data-[side=right]:!max-w-[420px]"
      >
        <form
          data-testid="access-add-sheet"
          onSubmit={(event) => void handleSubmit(event)}
          className="flex h-full flex-col"
        >
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">
              Zugriff auf „{entityLabel}" geben
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <PersonPicker
              testId="access-add-user"
              value={form.person}
              onSelect={updateField('person')}
              onClear={() => {
                updateField('person')(null)
              }}
              onRequestInvite={onRequestInvite}
              excludeIds={excludeIds}
              error={fieldError}
              errorPrefix="access-add"
              disabled={presetPerson !== undefined}
            />

            <RoleSelectField
              testId="access-add-role"
              errorPrefix="access-add"
              value={form.role}
              onChange={updateField('role')}
              error={fieldError}
            />

            {/* „Letzter Tag mit Zugriff" — `docs/FACHREGELN.md` Regel 9,
             * dieselbe Begründung wie in `AccessInviteSheet.tsx`. */}
            <FormField
              label="Letzter Tag mit Zugriff"
              testId="access-add-expires"
              apiField="expires_at"
              errorPrefix="access-add"
              type="date"
              value={form.expiresAt}
              onChange={updateField('expiresAt')}
              error={fieldError}
            />
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="access-add-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="access-add-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                data-testid="access-add-save"
                disabled={form.person === null || createMutation.isPending}
                aria-busy={createMutation.isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                Zugriff geben
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
